import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import Swal from 'sweetalert2';
import axiosInstance from '../../utils/axiosInstance';
import { getURL, getStudentPortalURL, getImageURL } from '../../utils/api';
import AllLocationsMap from '../../components/AllLocationsMap';
import { isInactiveRegistration, registrationStatusClass } from '../../utils/registrationStatus';

const STATUS_META = {
  pending: { label: 'Pending', badge: 'bg-amber-50 text-amber-700 border-amber-200', dot: 'bg-amber-400' },
  confirmed: { label: 'Confirmed', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: 'bg-emerald-400' },
  rejected: { label: 'Rejected', badge: 'bg-red-50 text-red-700 border-red-200', dot: 'bg-red-400' },
};

// A scan can sit at 'pending' while the student's attendance was already
// recorded some other way (e.g. a normal bulk "Mark Attendance" for the
// whole class happened before this scan got confirmed) -- shown instead of
// Confirm/Reject for that row, since confirming would just be redundant.
const ATTENDANCE_STATUS_META = {
  Present: { label: 'Present', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200', dot: 'bg-emerald-400' },
  Absent: { label: 'Absent', badge: 'bg-red-50 text-red-700 border-red-200', dot: 'bg-red-400' },
  Late: { label: 'Late', badge: 'bg-amber-50 text-amber-700 border-amber-200', dot: 'bg-amber-400' },
  Excused: { label: 'Excused', badge: 'bg-blue-50 text-blue-700 border-blue-200', dot: 'bg-blue-400' },
};

// A few student IDs are stored with a trailing space ("E278412 "), so students
// and scans are matched on the trimmed value. Otherwise a student who was just
// marked present can still show as "Not scanned".
const idKey = (id) => String(id ?? '').trim();

function initialsFromName(name) {
  if (!name) return '?';
  return name.trim().split(/\s+/).filter(Boolean).map((w) => w[0]).slice(0, 2).join('').toUpperCase();
}

function fmtDateTime(value) {
  if (!value) return '—';
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true,
  }).format(new Date(value));
}

function to12h(time24) {
  if (!time24) return '';
  const [h, m] = time24.split(':');
  let hours = parseInt(h, 10);
  const modifier = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  return `${hours}:${m} ${modifier}`;
}

function useCountdown(expiresAt) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  if (!expiresAt) return { label: '—', expired: false };
  const diff = new Date(expiresAt).getTime() - now;
  if (diff <= 0) return { label: 'Expired', expired: true };
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  return { label: h > 0 ? `${h}h ${m}m` : `${m}m ${s}s`, expired: false };
}

function ScanAvatar({ name, imagePath }) {
  const [err, setErr] = useState(false);
  if (!err && imagePath) {
    return (
      <img src={getImageURL(imagePath)} alt={name} onError={() => setErr(true)}
        className="h-10 w-10 shrink-0 rounded-full border border-slate-200 object-cover" />
    );
  }
  return (
    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-sm font-bold text-indigo-700">
      {initialsFromName(name)}
    </div>
  );
}

export default function AttendanceQr() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [session, setSession] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scans, setScans] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [confirmingAll, setConfirmingAll] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showLocationsMap, setShowLocationsMap] = useState(false);
  const [roster, setRoster] = useState(null);
  const [rosterLoading, setRosterLoading] = useState(true);
  const [isQrFullscreen, setIsQrFullscreen] = useState(false);
  const [rosterSearch, setRosterSearch] = useState('');
  const [markingPresentId, setMarkingPresentId] = useState(null);

  const closedRef = useRef(false);
  const qrFullscreenRef = useRef(null);

  // Tracks browser fullscreen state specifically for the QR panel (not the
  // whole page) so it can render a projector-friendly, larger layout while
  // fullscreen and revert automatically on Escape/exit.
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsQrFullscreen(document.fullscreenElement === qrFullscreenRef.current);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const handleToggleQrFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen?.();
    } else {
      qrFullscreenRef.current?.requestFullscreen?.();
    }
  };

  const scanUrl = useMemo(() => `${getStudentPortalURL()}/app/scan-attendance/${token}`, [token]);
  const countdown = useCountdown(session?.expiresAt);
  const isClosed = session?.status === 'closed' || countdown.expired;

  const loadScans = useCallback(async () => {
    try {
      const resp = await axiosInstance.get(`/admin/qr-attendance/sessions/${token}/scans`);
      setSession(resp.data?.session || null);
      setScans(resp.data?.scans || []);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load this QR attendance session.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadScans();
    const interval = setInterval(loadScans, 5000);
    return () => clearInterval(interval);
  }, [loadScans]);

  // Fetch the schedule display info once (doesn't change while polling scans).
  useEffect(() => {
    axiosInstance.get(`/admin/qr-attendance/sessions/${token}`)
      .then((resp) => setSchedule(resp.data?.schedule || null))
      .catch(() => {});
  }, [token]);

  // Full student roster for this schedule's batch(es) -- the same data the
  // "Mark Attendance" modal uses -- so the lecturer can see who has and
  // hasn't scanned yet, not just the scans that have come in.
  useEffect(() => {
    if (!schedule?.scheduleId) return;
    setRosterLoading(true);
    axiosInstance.get(`/admin/attendance/schedules/${schedule.scheduleId}/students`)
      .then((resp) => setRoster(resp.data?.batches || []))
      .catch(() => setRoster([]))
      .finally(() => setRosterLoading(false));
  }, [schedule?.scheduleId]);

  // Best-effort close when the lecturer navigates away or closes this tab --
  // sendBeacon fires reliably during unload, unlike a normal fetch/axios call.
  useEffect(() => {
    const closeBeacon = () => {
      if (closedRef.current) return;
      const url = `${getURL()}/qr-attendance/public/sessions/${token}/close`;
      navigator.sendBeacon?.(url);
    };
    window.addEventListener('pagehide', closeBeacon);
    return () => {
      window.removeEventListener('pagehide', closeBeacon);
    };
  }, [token]);

  const handleManualClose = async () => {
    try {
      await axiosInstance.post(`/admin/qr-attendance/sessions/${token}/close`);
      closedRef.current = true;
      loadScans();
    } catch {
      // non-fatal
    }
  };

  const handleConfirm = async (scan) => {
    setBusyId(scan.id);
    try {
      await axiosInstance.post(`/admin/qr-attendance/scans/${scan.id}/confirm`);
      loadScans();
    } catch (err) {
      window.alert(err?.response?.data?.message || 'Failed to confirm this scan.');
    } finally {
      setBusyId(null);
    }
  };

  const handleReject = async (scan) => {
    const result = await Swal.fire({
      title: 'Reject this scan?',
      html: `<strong>${scan.studentName || scan.studentId}</strong>'s scan will not be recorded as attendance.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, reject it',
      cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;

    setBusyId(scan.id);
    try {
      await axiosInstance.post(`/admin/qr-attendance/scans/${scan.id}/reject`);
      loadScans();
    } catch (err) {
      window.alert(err?.response?.data?.message || 'Failed to reject this scan.');
    } finally {
      setBusyId(null);
    }
  };

  const handleMarkPresent = async (student) => {
    const result = await Swal.fire({
      title: 'Mark this student present?',
      html: `<strong>${student.studentName || student.studentId}</strong> will be marked <strong class="text-emerald-600">Present</strong> for this class without scanning the QR.`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#059669',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, mark present',
      cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;

    setMarkingPresentId(student.studentId);
    try {
      await axiosInstance.post(`/admin/qr-attendance/sessions/${token}/manual-present`, { studentId: student.studentId });
      loadScans();
    } catch (err) {
      window.alert(err?.response?.data?.message || 'Failed to mark this student present.');
    } finally {
      setMarkingPresentId(null);
    }
  };

  const handleConfirmAll = async () => {
    const result = await Swal.fire({
      title: 'Finalize attendance for this class?',
      html: `
        <div class="text-left text-sm text-gray-600">
          <p><strong>${counts.pending}</strong> pending scan${counts.pending === 1 ? '' : 's'} will be marked <strong class="text-emerald-600">Present</strong>.</p>
          <p class="mt-2">Every other student in this class's batch(es) who hasn't scanned (or whose scan was rejected) will be marked <strong class="text-red-600">Absent</strong>.</p>
        </div>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#4f46e5',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, finalize attendance',
      cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;

    setConfirmingAll(true);
    try {
      const resp = await axiosInstance.post(`/admin/qr-attendance/sessions/${token}/confirm-all`);
      loadScans();
      Swal.fire({
        title: 'Attendance finalized',
        html: `<strong class="text-emerald-600">${resp?.data?.confirmedCount ?? 0}</strong> marked Present via QR scan &middot; <strong class="text-red-600">${resp?.data?.absentCount ?? 0}</strong> marked Absent`,
        icon: 'success',
        confirmButtonColor: '#4f46e5',
      });
    } catch (err) {
      window.alert(err?.response?.data?.message || 'Failed to confirm all pending scans.');
    } finally {
      setConfirmingAll(false);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(scanUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const counts = useMemo(() => {
    const c = { pending: 0, confirmed: 0, rejected: 0 };
    scans.forEach((s) => { c[s.status] = (c[s.status] || 0) + 1; });
    return c;
  }, [scans]);

  const scannedStudentIds = useMemo(() => new Set(scans.map((s) => idKey(s.studentId))), [scans]);
  const scanByStudentId = useMemo(() => {
    const map = new Map();
    scans.forEach((s) => map.set(idKey(s.studentId), s));
    return map;
  }, [scans]);
  const totalRosterCount = useMemo(
    () => (roster || []).reduce((sum, b) => sum + (b.students?.length || 0), 0),
    [roster]
  );
  const rosterScannedCount = useMemo(
    () => (roster || []).reduce(
      (sum, b) => sum + (b.students || []).filter((st) => scannedStudentIds.has(idKey(st.studentId))).length,
      0
    ),
    [roster, scannedStudentIds]
  );

  // Highest attendance percentage first, lowest last.
  const sortedRoster = useMemo(() => {
    if (!roster) return roster;
    return roster.map((batch) => {
      const students = [...(batch.students || [])].sort(
        (a, b) => (Number(b.attendancePercentage) || 0) - (Number(a.attendancePercentage) || 0)
      );
      return { ...batch, students };
    });
  }, [roster]);

  const filteredRoster = useMemo(() => {
    if (!sortedRoster) return sortedRoster;
    const q = rosterSearch.trim().toLowerCase();
    if (!q) return sortedRoster;
    return sortedRoster
      .map((batch) => ({
        ...batch,
        students: (batch.students || []).filter((st) =>
          (st.studentName || '').toLowerCase().includes(q)
          || (st.studentId || '').toLowerCase().includes(q)
          || (st.registrationNumber || '').toLowerCase().includes(q)
        ),
      }))
      .filter((batch) => batch.students.length > 0);
  }, [sortedRoster, rosterSearch]);

  const handleViewAllLocations = () => setShowLocationsMap(true);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 text-slate-400">
        <svg className="h-8 w-8 animate-spin text-indigo-500" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
        </svg>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-slate-50 px-6 text-center text-sm text-slate-500">
        <p>{error}</p>
        <button onClick={() => navigate('/lecturer/attendance')} className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white">
          Back to Attendance
        </button>
      </div>
    );
  }

  const subjectLabel = schedule?.subjectFullName || schedule?.subjectShortName || 'Class';
  const batchLabel = schedule?.batchGroupId
    ? (schedule?.batchGroupWhatsappName || schedule?.batchGroupName)
    : (schedule?.whatsappGroupName || schedule?.batchInternalName);

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white px-6 py-4 shadow-sm">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-indigo-600">QR Attendance · Live Session</p>
            <h1 className="text-lg font-bold text-slate-900">{subjectLabel}</h1>
            <p className="text-xs text-slate-500">
              {batchLabel}
              {schedule?.startTime ? ` · ${to12h(schedule.startTime)} - ${to12h(schedule.endTime)}` : ''}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className={`rounded-lg border px-3 py-1.5 text-xs font-semibold ${isClosed ? 'border-red-200 bg-red-50 text-red-700' : 'border-emerald-200 bg-emerald-50 text-emerald-700'}`}>
              {isClosed ? 'Closed' : `Live · Expires in ${countdown.label}`}
            </div>
            {!isClosed && (
              <button
                onClick={handleManualClose}
                className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"
              >
                Close QR
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-8">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[380px_1fr]">
          {/* QR card */}
          <div className="h-fit rounded-2xl border border-slate-200 bg-white p-6 text-center shadow-sm">
            {isClosed ? (
              <div className="flex flex-col items-center gap-3 py-10">
                <svg className="h-12 w-12 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                </svg>
                <p className="text-sm font-medium text-slate-500">This QR code is closed.<br />Students can no longer scan for this class.</p>
              </div>
            ) : (
              <>
                <div
                  ref={qrFullscreenRef}
                  className={isQrFullscreen
                    ? 'flex h-full w-full flex-col items-center justify-center bg-white p-8'
                    : ''}
                >
                  {isQrFullscreen && (
                    <div className="mb-8 text-center">
                      <p className="text-lg font-semibold uppercase tracking-wide text-indigo-600">{subjectLabel}</p>
                      <p className="mt-1 text-2xl font-bold text-slate-900">{batchLabel}</p>
                    </div>
                  )}
                  <div className={`mx-auto flex w-fit items-center justify-center rounded-xl border-4 border-slate-100 bg-white ${isQrFullscreen ? 'p-8' : 'p-4'}`}>
                    <QRCodeSVG value={scanUrl} size={isQrFullscreen ? 480 : 220} level="M" includeMargin={false} />
                  </div>
                  <p className={isQrFullscreen ? 'mt-8 text-2xl font-semibold text-slate-700' : 'mt-4 text-sm font-semibold text-slate-700'}>
                    Students scan this with their phone camera
                  </p>
                  {isQrFullscreen && (
                    <div className="mt-3 flex items-center gap-1.5 text-xs font-medium text-slate-400">
                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                      {totalRosterCount > 0 ? `${rosterScannedCount} / ${totalRosterCount} scanned` : `${scans.length} scanned`}
                    </div>
                  )}
                  {isQrFullscreen && (
                    <button
                      onClick={handleToggleQrFullscreen}
                      className="mt-6 rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-500 hover:bg-slate-50"
                    >
                      Exit Full Screen
                    </button>
                  )}
                </div>
                {!isQrFullscreen && (
                  <>
                    <p className="mt-1 text-xs text-slate-400">Opens in their student portal — location must be turned on to scan.</p>
                    <div className="mt-3 flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                      <span className="min-w-0 flex-1 truncate text-left text-xs text-slate-500">{scanUrl}</span>
                      <button
                        onClick={handleCopyLink}
                        className={`shrink-0 rounded-md px-2 py-1 text-[11px] font-semibold transition ${copied ? 'bg-emerald-100 text-emerald-700' : 'bg-white text-indigo-600 hover:bg-indigo-50'}`}
                      >
                        {copied ? 'Copied!' : 'Copy'}
                      </button>
                    </div>
                    <button
                      onClick={handleToggleQrFullscreen}
                      className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg border border-indigo-200 bg-white px-4 py-2 text-sm font-semibold text-indigo-600 hover:bg-indigo-50"
                    >
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4 8V6a2 2 0 012-2h2M4 16v2a2 2 0 002 2h2m8-16h2a2 2 0 012 2v2m-4 12h2a2 2 0 002-2v-2" />
                      </svg>
                      Full Screen (for projector)
                    </button>
                  </>
                )}
              </>
            )}
          </div>

          {/* Live scans */}
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-5 py-4">
              <h2 className="text-sm font-bold text-slate-800">Scans</h2>
              <div className="flex items-center gap-2 text-xs font-semibold">
                <span className="rounded-full bg-amber-50 px-2.5 py-1 text-amber-700">{counts.pending} pending</span>
                <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-emerald-700">{counts.confirmed} confirmed</span>
                <span className="rounded-full bg-red-50 px-2.5 py-1 text-red-700">{counts.rejected} rejected</span>
                {scans.length > 0 && (
                  <button
                    onClick={handleViewAllLocations}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"
                  >
                    <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
                    </svg>
                    View All Locations
                  </button>
                )}
                {counts.pending > 0 && (
                  <button
                    onClick={handleConfirmAll}
                    disabled={confirmingAll}
                    title="Marks pending scans Present and everyone else in the batch(es) Absent"
                    className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {confirmingAll ? 'Finalizing…' : 'Confirm All & Finalize'}
                  </button>
                )}
              </div>
            </div>

            {scans.length === 0 ? (
              <div className="flex flex-col items-center gap-2 px-5 py-16 text-center text-sm text-slate-400">
                <svg className="h-10 w-10 text-slate-200" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
                </svg>
                Waiting for students to scan…
              </div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {scans.map((s) => {
                  const alreadyMarked = s.status === 'pending' && Boolean(s.markedAttendanceStatus);
                  const isManual = s.source === 'manual';
                  const hasLocation = s.latitude != null && s.longitude != null;
                  const meta = alreadyMarked
                    ? (ATTENDANCE_STATUS_META[s.markedAttendanceStatus] || ATTENDANCE_STATUS_META.Present)
                    : isManual
                      ? { label: 'Manual Present', badge: 'bg-indigo-50 text-indigo-700 border-indigo-200', dot: 'bg-indigo-400' }
                      : (STATUS_META[s.status] || STATUS_META.pending);
                  const mapsUrl = `https://maps.google.com/?q=${s.latitude},${s.longitude}`;
                  return (
                    <li key={s.id} className="flex flex-wrap items-center gap-4 px-5 py-4">
                      <ScanAvatar name={s.studentName} imagePath={s.studentImage} />
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-sm font-semibold text-slate-900">{s.studentName || s.studentId}</span>
                          <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${meta.badge}`}>
                            <span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`} />
                            {meta.label}
                          </span>
                        </div>
                        <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-slate-500">
                          <span className="font-semibold text-indigo-600">{s.studentId}</span>
                          {s.registrationNumber && <span>Reg #: {s.registrationNumber}</span>}
                          {hasLocation && (
                            <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className="text-indigo-500 underline decoration-dotted">
                              View location{s.accuracy ? ` (±${Math.round(s.accuracy)}m)` : ''}
                            </a>
                          )}
                          <span>{isManual ? 'Marked' : 'Scanned'} {fmtDateTime(s.scannedAt)}</span>
                        </div>
                        {s.status === 'confirmed' && isManual && (
                          <p className="mt-1 text-xs font-medium text-indigo-600">
                            Manual present by {s.confirmedByName || 'lecturer'} · {fmtDateTime(s.confirmedAt)}
                          </p>
                        )}
                        {s.status === 'confirmed' && !isManual && (
                          <p className="mt-1 text-xs font-medium text-emerald-600">
                            Confirmed by {s.confirmedByName || 'lecturer'} · {fmtDateTime(s.confirmedAt)}
                          </p>
                        )}
                        {s.status === 'rejected' && (
                          <p className="mt-1 text-xs font-medium text-red-500">
                            Rejected by {s.confirmedByName || 'lecturer'} · {fmtDateTime(s.confirmedAt)}
                          </p>
                        )}
                        {alreadyMarked && (
                          <p className="mt-1 text-xs font-medium text-slate-400">
                            Attendance already marked as {s.markedAttendanceStatus} outside this QR session
                          </p>
                        )}
                      </div>
                      {s.status === 'pending' && !alreadyMarked && (
                        <div className="flex shrink-0 gap-2">
                          <button
                            onClick={() => handleConfirm(s)}
                            disabled={busyId === s.id}
                            className="rounded-lg bg-emerald-100 px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-200 disabled:opacity-50"
                          >
                            Confirm
                          </button>
                          <button
                            onClick={() => handleReject(s)}
                            disabled={busyId === s.id}
                            className="rounded-lg bg-red-100 px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-200 disabled:opacity-50"
                          >
                            Reject
                          </button>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        {/* Full batch roster -- who's supposed to be here, scanned or not */}
        <div className="mt-6 rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-5 py-4">
            <h2 className="text-sm font-bold text-slate-800">Batch Student List</h2>
            <div className="flex items-center gap-3">
              {!rosterLoading && (
                <span className="text-xs font-semibold text-slate-500">
                  {rosterScannedCount} / {totalRosterCount} scanned
                </span>
              )}
              <input
                value={rosterSearch}
                onChange={(e) => setRosterSearch(e.target.value)}
                placeholder="Search name, reg. no, student ID…"
                className="w-64 rounded-lg border border-slate-200 px-3 py-1.5 text-sm outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
              />
            </div>
          </div>

          {rosterLoading ? (
            <div className="flex items-center justify-center py-10 text-slate-400">
              <svg className="h-6 w-6 animate-spin text-indigo-400" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
            </div>
          ) : !roster || totalRosterCount === 0 ? (
            <div className="px-5 py-10 text-center text-sm text-slate-400">No students found for this schedule.</div>
          ) : !filteredRoster || filteredRoster.length === 0 ? (
            <div className="px-5 py-10 text-center text-sm text-slate-400">No students match your search.</div>
          ) : (
            <div className="divide-y divide-slate-100">
              {filteredRoster.map((batch) => (
                <div key={batch.batchId}>
                  <div className="bg-slate-50 px-5 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    {batch.whatsappGroupName || batch.batchInternalName || batch.batchName} &middot; {batch.students.length} student{batch.students.length !== 1 ? 's' : ''}
                  </div>
                  <ul className="divide-y divide-slate-50">
                    {batch.students.map((st) => {
                      const scan = scanByStudentId.get(idKey(st.studentId));
                      const isConfirmed = scan?.status === 'confirmed';
                      const isManual = isConfirmed && scan?.source === 'manual';
                      // Scanned, but still waiting for the lecturer to confirm it above.
                      const isPending = scan?.status === 'pending';
                      const isRejected = scan?.status === 'rejected';
                      return (
                        <li
                          key={st.studentId}
                          className={`flex items-center gap-4 px-5 py-3 ${isInactiveRegistration(st.registrationStatus) ? 'bg-red-50 ring-1 ring-inset ring-red-200' : ''}`}
                        >
                          <ScanAvatar name={st.studentName} imagePath={st.studentImage} />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-semibold text-slate-900">{st.studentName}</p>
                            <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-slate-500">
                              <span className="font-semibold text-indigo-600">{st.studentId}</span>
                              {st.registrationNumber && <span>Reg #: {st.registrationNumber}</span>}
                              {st.registrationStatus && (
                                <span className={`rounded px-1.5 py-0.5 text-[11px] font-semibold ${registrationStatusClass(st.registrationStatus)}`}>
                                  {st.registrationStatus}
                                </span>
                              )}
                            </div>
                            <div className="mt-1.5 flex items-center gap-2 max-w-[220px]">
                              <div className="h-1.5 flex-1 rounded-full bg-slate-100 overflow-hidden">
                                <div
                                  className={`h-full rounded-full ${
                                    st.attendancePercentage >= 75 ? 'bg-emerald-500' :
                                    st.attendancePercentage >= 50 ? 'bg-amber-400' :
                                    'bg-red-400'
                                  }`}
                                  style={{ width: `${Math.min(100, Math.max(0, st.attendancePercentage))}%` }}
                                />
                              </div>
                              <span className="shrink-0 text-[11px] font-medium text-slate-500">{st.attendancePercentage}%</span>
                            </div>
                          </div>
                          {isConfirmed ? (
                            <span className={`shrink-0 rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${
                              isManual ? 'border-indigo-200 bg-indigo-50 text-indigo-700' : 'border-emerald-200 bg-emerald-50 text-emerald-700'
                            }`}>
                              {isManual ? 'Manual Present' : 'Scanned'}
                            </span>
                          ) : isPending ? (
                            // No "Mark Present" here: it would overwrite the student's real
                            // scan (and its location) with a manual one. Confirm it above.
                            <span
                              title="Waiting for you to confirm this scan"
                              className="shrink-0 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-amber-700"
                            >
                              Scanned · Pending
                            </span>
                          ) : (
                            <div className="flex shrink-0 items-center gap-2">
                              {isRejected ? (
                                <span className="rounded-full border border-red-200 bg-red-50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-red-700">
                                  Scan rejected
                                </span>
                              ) : (
                                <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-slate-400">
                                  Not scanned
                                </span>
                              )}
                              <button
                                onClick={() => handleMarkPresent(st)}
                                disabled={markingPresentId === st.studentId}
                                className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
                              >
                                {markingPresentId === st.studentId ? 'Marking…' : 'Mark Present'}
                              </button>
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {showLocationsMap && (
        <AllLocationsMap scans={scans} onClose={() => setShowLocationsMap(false)} />
      )}
    </div>
  );
}
